import { Link } from "react-router-dom";
import { useLang } from "@/context/LanguageContext";
import { useSettings } from "@/context/SettingsContext";
import { AMNAYA_PEETHAS, SHANKARA_IMG, peethaWithOverrides, shankaraOverrides } from "@/lib/amnayaPeethas";
import Seo from "@/components/Seo";
import { Compass, ArrowRight, BookOpen, User } from "lucide-react";

const DIR_POS = { sringeri: "south", dwaraka: "west", puri: "east", jyotirmath: "north" };

export default function AmnayaPeethas() {
  const { pick } = useLang();
  const settings = useSettings();
  const sh = shankaraOverrides(settings);
  const heroImg = sh.image || SHANKARA_IMG;

  return (
    <div data-testid="amnaya-peethas-page" className="bg-[var(--bg)]">
      <Seo path="/amnaya-peethas" title="The Four Amnaya Peethas of Adi Shankaracharya — Dharmartha" description="The four cardinal maths (Sringeri, Dwaraka, Puri, Jyotirmath) established by Sri Adi Shankaracharya, with their Veda, Mahavakya and current Acharyas." />

      {/* Hero — Adi Shankaracharya */}
      <section className="relative overflow-hidden border-b border-line bg-[var(--ink)]">
        <div className="mx-auto grid max-w-6xl gap-8 px-5 py-14 md:grid-cols-[1fr_0.8fr] md:items-center md:px-8 md:py-20">
          <div className="text-parchment">
            <p className="overline-label text-gold">{pick("जगद्गुरु", "Jagadguru")}</p>
            <h1 className="mt-3 font-deva text-3xl leading-tight text-white deva-lh md:text-5xl">{pick("श्री आदि शंकराचार्य", "Sri Adi Shankaracharya")}</h1>
            <p className="mt-2 font-serif-latin text-xl text-gold">{pick("चार आम्नाय पीठ", "The Four Amnaya Peethas")}</p>
            <p className="mt-5 max-w-xl text-[15px] leading-relaxed text-white/80">
              {pick(
                "आठवीं शताब्दी के महान अद्वैत वेदान्त आचार्य श्री आदि शंकराचार्य ने भारत की चार दिशाओं में चार आम्नाय पीठों की स्थापना की — जिससे सनातन धर्म एकसूत्र में बँधा रहे। प्रत्येक पीठ एक वेद, एक महावाक्य एवं एक सम्प्रदाय का संरक्षक है।",
                "Sri Adi Shankaracharya, the great 8th-century master of Advaita Vedanta, established four Āmnāya Pīṭhas in the four directions of Bhārata to unify and preserve Sanātana Dharma. Each maṭha is the guardian of one Veda, one Mahāvākya and one lineage of Jagadgurus.",
              )}
            </p>
          </div>
          <div className="relative mx-auto w-full max-w-xs overflow-hidden rounded-xl border-4 border-[var(--gold)]/50 shadow-2xl">
            <img src={heroImg} alt="Sri Adi Shankaracharya" className="w-full object-cover" />
          </div>
        </div>
      </section>

      {/* Four peethas */}
      <section className="mx-auto max-w-6xl px-5 py-14 md:px-8 md:py-16">
        <div className="mb-8 flex items-center gap-2">
          <Compass className="h-5 w-5 text-saffron" />
          <h2 className="font-serif-latin text-2xl text-ink md:text-3xl">{pick("चार दिशाओं के पीठ", "The Four Cardinal Maths")}</h2>
        </div>
        <div className="grid gap-6 md:grid-cols-2">
          {AMNAYA_PEETHAS.map((base) => {
            const p = peethaWithOverrides(base, settings);
            return (
              <Link key={p.slug} to={`/amnaya-peethas/${p.slug}`} data-testid={`peetha-card-${p.slug}`}
                className="group flex flex-col overflow-hidden rounded-xl border border-line bg-[var(--surface)] transition-all duration-300 hover:-translate-y-1 hover:border-[var(--gold)] hover:shadow-xl">
                <div className="relative aspect-[16/10] overflow-hidden bg-[var(--parchment)]">
                  <img src={p.image} alt={p.name} loading="lazy" className="h-full w-full object-cover transition-transform duration-500 group-hover:scale-105" />
                  <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-black/10 to-transparent" />
                  <span className="absolute left-3 top-3 rounded-full bg-black/45 px-3 py-1 text-xs font-medium uppercase tracking-wider text-white backdrop-blur">{p.direction_en} · {DIR_POS[p.slug]}</span>
                  <div className="absolute inset-x-0 bottom-0 p-4">
                    <p className="font-deva text-xl text-white deva-lh drop-shadow">{p.deva_name}</p>
                    <p className="font-serif-latin text-base text-white/90">{p.name}</p>
                  </div>
                </div>
                <div className="flex flex-1 flex-col p-5">
                  <div className="flex flex-wrap gap-2 text-xs">
                    <span className="inline-flex items-center gap-1 rounded-full bg-saffron/10 px-2.5 py-1 text-saffron"><BookOpen className="h-3 w-3" /> {p.veda}</span>
                    <span className="rounded-full bg-gold/15 px-2.5 py-1 text-[var(--ink)]">“{p.mahavakya}”</span>
                  </div>
                  <p className="mt-3 flex-1 text-sm leading-relaxed text-ink-2 line-clamp-3">{p.summary}</p>
                  <p className="mt-3 flex items-center gap-1.5 text-xs text-ink-3"><User className="h-3.5 w-3.5" /> {p.current_acharya}</p>
                  <span className="mt-3 inline-flex items-center gap-1 text-sm font-medium text-saffron transition-transform group-hover:translate-x-0.5">{pick("विस्तार से जानें", "Learn more")} <ArrowRight className="h-4 w-4" /></span>
                </div>
              </Link>
            );
          })}
        </div>
      </section>
    </div>
  );
}
